import React, { useState, useEffect } from 'react';
import { DevConfig } from '../../../config/environment/devConfig';
import { gameLogger } from '../../../utils/GameLogger';
import { Helix2000Character } from '../../../shared/CharacterTypes';
import type { ActionBarAssignment } from '../../../shared/types/Input';
import type { SlotRef } from '../../../shared/hotbar/barLayout';
import { HUDPanel } from './components/HUDPanel';
import {
  HUD_PANEL_DEFAULTS,
  calculateX,
  calculateY
} from '../../../config/ui/hudDefaults.config';
import { useHudTopLayout } from './hudTopLayout';
import { useHudCompactLayout, useHudMinimapRect } from './hudCompactLayout';
import { useControllerNavigation } from '../input/controller/ControllerNavigationContext';
import { DebugToolbox } from './DevBar/DebugToolbox';
import { ActionBarHudCluster } from '../Systems/ActionBar/ActionBarHudCluster';
import { MiniStatsDisplay } from '../Systems/CharacterStatsUI';
import { MiniJobDisplay } from '../Systems/JobAdvancementUI';
import { StarCounter } from '../Systems/StarCounter';
import { QuickMenu } from './components/QuickMenu';
import { EditorReturnPill } from './components/EditorReturnPill';
import { PlayerInfoPanel } from './PlayerInfoPanel';
import { ChatPanel } from './ChatPanel';
import { QuestTracker } from './QuestTracker';
import { releaseHudPointerFocus } from './hudPointerFocus';
import { BossBar } from './BossBar';
import { BuffBar } from './BuffBar';
import { GmEventAnnouncementBar } from '../Windows/GmEvent/GmEventAnnouncement';
import { FishingMeter } from '../Windows/GmEvent/FishingMeter';
import { FishingCatchNotification } from '../Windows/GmEvent/FishingCatchNotification';
import { RaceProgressPanel } from '../Windows/GmEvent/RaceProgressPanel';
import { RaceFinishNotification } from '../Windows/GmEvent/RaceFinishNotification';
import { RaceLobbyAutoOpener } from '../Windows/GmEvent/RaceLobbyAutoOpener';
import { RaceCountdownOverlay } from '../Windows/GmEvent/RaceCountdownOverlay';
import { RaceResultsHud } from '../Windows/GmEvent/RaceResultsHud';
import { RaceHubReturn } from './RaceHubReturn';
import { SpeedrunTimerHud } from './SpeedrunTimerHud';
import { SpeedrunFinishReceipt } from './SpeedrunFinishReceipt';
import { storage } from '../../services/StorageService';
import { useActionFailureFeedback } from '../hooks/useActionFailureFeedback';
import { useHudTomeVibe } from './useHudTomeVibe';
import './tome/hud-tome-base.css';
import './tome/hud-tome-a.css';
import './tome/hud-tome-b.css';
import './tome/hud-tome-c.css';
import { isTouchControlsEnabled } from '../mobile/touchControlsFlag';
import {
  debugToolboxContainerStyle,
  mapBannerStyle,
  mapBannerIconStyle,
  mapIdStyle,
  connectionStatusStyle,
  connectionStatusTitleStyle,
  connectionStatusMessageStyle,
  chatDockStyle,
  collapsedHUDStyle,
  quickMenuContainerStyle,
  hudGlobalStyles
} from './HUD.styles';

interface HUDProps {
  player: Helix2000Character | null;
  isConnected: boolean;
  isHotbarHydrated?: boolean;
  disconnectReason?: string | null;
  levelName?: string;
  levelId?: string;
  onAction?: (action: string, data?: any) => void;
  onOpenStats?: () => void;
  onOpenJobAdvancement?: () => void;
  onOpenAbilities?: () => void;

  isDevToolsActive?: boolean;
  isAbilitiesActive?: boolean;
}

export function buildActionBarUpdate(
  slotOrRef: number | SlotRef,
  assignment: ActionBarAssignment | null,
): { slotIndex: number; bar?: number; page?: number; abilityId?: string; itemId?: string } {
  const isRef = typeof slotOrRef === 'object' && slotOrRef !== null;
  const slotIndex = isRef ? slotOrRef.slot : slotOrRef;
  const refFields = isRef ? { bar: slotOrRef.bar, page: slotOrRef.page } : {};
  if (assignment?.type === 'ability') {
    return { slotIndex, ...refFields, abilityId: assignment.abilityId };
  }
  if (assignment?.type === 'item') {
    return { slotIndex, ...refFields, itemId: assignment.itemId };
  }
  return { slotIndex, ...refFields };
}

export const HUD: React.FC<HUDProps> = ({
  player,
  isConnected,
  isHotbarHydrated = false,
  disconnectReason,
  levelName,
  levelId,
  onAction,
  onOpenStats,
  onOpenJobAdvancement,
  onOpenAbilities,
  isDevToolsActive,
  isAbilitiesActive = false
}) => {
  useActionFailureFeedback();
  // Plan 17: the HUD root publishes the zone's tome tokens (--tome-*, --tome-hud-*); changes per map switch only.
  const hudTome = useHudTomeVibe(levelId);
  // Multiplayer debug-label visibility — persisted UserPrefs.dev preference
  // (the last UIContext flag, retired 2026-06-12). HUD reads/writes through
  // the prefs bus only; the netobs MultiplayerLabelsReporter applies it to
  // the diagnostics runtime (dep-cruiser forbids HUD→diagnostics imports).
  const [mpLabelsEnabled, setMpLabelsEnabled] = useState(
    () => storage.loadUserPrefs().dev.showMultiplayerLabels,
  );
  useEffect(
    () => storage.subscribeUserPrefs((p) => setMpLabelsEnabled(p.dev.showMultiplayerLabels)),
    [],
  );
  // Compact landscape band stack (W2a, 2026-09-28): at heights ≤ 450 px the
  // action bar scales, the chat docks as a right-column peek above the band,
  // and the expanded quick menu anchors below the banner clearance. The
  // banner clearance is the SAME live measurement the target frame uses
  // (useHudTopLayout → computeBannerClearanceTop), so both surfaces stay below
  // the banner by construction. Heights > 450 return isCompact:false and keep
  // the accepted desktop layout byte-for-byte.
  //
  // W2a-2 (2026-09-28): the expanded quick menu also derives its right inset
  // (and, when the below-minimap slot is too short, its anchor) from the LIVE
  // minimap rect — layout-time measurement only (useHudMinimapRect), so the
  // per-frame path stays DOM-free. The minimap is the top-right fixed surface;
  // the panel either sits below it or slides left of it, never behind it.
  const hasBanner = DevConfig.systems.mapBanner && !!levelName;
  const hasDebugText = DevConfig?.systems?.debugOverlay ?? false;
  const topLayout = useHudTopLayout(hasBanner, hasDebugText);
  const minimapRect = useHudMinimapRect();
  const compact = useHudCompactLayout({
    bannerClearanceTop: topLayout.bannerClearanceTop,
    minimapRect,
  });
  const { quickMenuOpen } = useControllerNavigation();
  const compactChatInset = compact.isCompact
    ? { right: compact.chatRightInset, bottom: compact.chatBottomInset }
    : null;
  // Expanded quick menu in compact mode sits ABOVE the chat peek (right column
  // stack), clear of the measured minimap (W2a-2); collapsed, the ≡ launcher
  // keeps its thumb-reachable base dock.
  const quickMenuBottomInset = compact.isCompact && quickMenuOpen
    ? compact.quickMenuBottomInset
    : HUD_PANEL_DEFAULTS.quickMenu.position.y;
  const quickMenuRightInset = compact.isCompact && quickMenuOpen
    ? compact.quickMenuRightInset
    : HUD_PANEL_DEFAULTS.quickMenu.position.offset;
  const toggleMultiplayerLabels = () => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({
      ...cur,
      dev: { ...cur.dev, showMultiplayerLabels: !cur.dev.showMultiplayerLabels },
    });
  };
  // NOTE (Hollow Coil Lane F, 2026-07-01): the HUD used to hide the action bar
  // + system panels while `tutorialActive` (a progressive-reveal design that
  // was dead code — the flag was never set true). The Pig Beach FTUE beats
  // explicitly reference the hotbar ("drag it to your hotbar") and the K
  // panel, so the full HUD now stays visible during the tutorial;
  // `tutorialActive` only gates the TutorialOverlay toast (GameUIOverlay).
  const [isExpanded] = useState(true);
  // Connection-status banner dismiss — NOT a window (the HUD-local window
  // toggles all folded onto the engine 2026-06-12, ratchet 6→0).
  const [connectionBannerVisible, setConnectionBannerVisible] = useState(true);

  // Only log when player data actually changes, not on every render
  useEffect(() => {
    if (player) {
      gameLogger.info('game', `[HUD] Player updated: ${player.name}`);
      gameLogger.debug('game', '[HUD] ActionBar check:', {
        actionBarEnabled: DevConfig.systems.actionBar,
        hasAbilities: !!player.abilities,
        abilityCount: player.abilities?.length || 0,
        actionBarSlots: player.actionBar?.slots?.filter((s: any) => s.type !== 'empty').length || 0
      });
    }
  }, [player?.id, player?.name, player?.abilities?.length]);

  // The showMultiplayerLabels → diagnostics-runtime sync lives in
  // <MultiplayerLabelsReporter/> (ui/netobs/, mounted in main.tsx) so the
  // gameplay HUD no longer imports the diagnostics layer.

  if (!player) return null;

  if (DevConfig.zenMode) {
    return <div className="hud-container" />;
  }

  const hudRootStyleAttr = hudTome.hudStyle === 'classic' ? undefined : (hudTome.hudStyle === 'mix' ? 'mix' : hudTome.hudStyle);

  return (
    <div className={`hud-container ${hudTome.className}`} data-hud-style={hudRootStyleAttr} style={hudTome.style}
      onClickCapture={releaseHudPointerFocus}>
      {/* Debug Toolbox */}
      {DevConfig.systems.debugToolbox && (
        <div style={debugToolboxContainerStyle}>
          <DebugToolbox
            isDevToolsActive={isDevToolsActive}
            isAbilitiesActive={isAbilitiesActive}
            isMultiplayerLabelsActive={mpLabelsEnabled}
            onAction={(a: string, d?: any) => {
              // Window toggles route DIRECTLY through windowBridge inside
              // DebugToolbox now (HUD fold) — the old local-handle-then-
              // forward pattern double-opened two different windows per
              // click (🧪 opened SimulationToolsWindow AND simulationPanel).
              if (a === 'toggle_abilities') {
                onOpenAbilities?.();
              } else if (a === 'toggle_multiplayer_labels') {
                toggleMultiplayerLabels();
              }
              onAction?.(a, d);
            }}
          />
        </div>
      )}

      {/* Map Banner */}
      {DevConfig.systems.mapBanner && levelName && (
        <div id="hud-map-banner" data-testid="map-banner" style={mapBannerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={mapBannerIconStyle}>🗺️</span>
            {levelName}
          </div>
          {/* Raw levelId is a dev detail — hidden in normal play (HUD-restyle
              §5), kept only when the debug overlay preset flag is on. */}
          {levelId && DevConfig.systems.debugOverlay && (
            <span style={mapIdStyle}>{levelId}</span>
          )}
        </div>
      )}

      {/* Back-to-editor pill — only on the editor's test-play surface
          (?fromEditor=1, set by the editor's Test button). */}
      <EditorReturnPill />

      {/* Boss bar — top-center set-piece; self-shows only while a boss-phased
          mob is transitioning phases (HUD-restyle Lane D.3). Mounted outside
          the isExpanded branch so a boss fight surfaces even if the HUD is
          collapsed. */}
      <BossBar />

      {/* GM Event announcement banner — top-center overlay showing the
          active event's title, phase, countdown, and participant count.
          Renders null when no event is active. */}
      <GmEventAnnouncementBar />

      {/* GM Event mode-specific HUD overlays — each renders null when no
          relevant event/state is active. */}
      <FishingMeter />
      <FishingCatchNotification />
      <RaceProgressPanel />
      <RaceFinishNotification />

      {/* GM Race lobby windows — auto-opened/closed by the window substrate.
          The RaceLobbyAutoOpener monitors the room schema and toggles the
          'raceLobby' gameplay window. The countdown overlay is still an
          always-on-top HUD element for the staging phase. */}
      <RaceLobbyAutoOpener />
      <RaceCountdownOverlay />
      <RaceResultsHud />
      {/* Post-race lobby exit for players (E.4) — renders null off the
          post-lobby map and during staging/live. */}
      <RaceHubReturn />

      {/* Persistent owner timer for the Relight the Coil category. It is fed
          by a reconnect-safe server projection and opens the records window. */}
      <SpeedrunTimerHud />
      <SpeedrunFinishReceipt />

      {/* Connection Status & Debug Info */}
      {DevConfig.systems.connectionStatus && !isConnected && connectionBannerVisible && (() => {
        // Parse close code from disconnect reason (e.g. "Left room: 4001")
        const codeMatch = disconnectReason?.match(/(\d{4})/);
        const closeCode = codeMatch ? parseInt(codeMatch[1], 10) : null;

        // Map error codes to actionable messages
        const errorInfo: Record<number, { title: string; action: string; color: string }> = {
          4001: { title: '🔑 Authentication Failed', action: 'Your session has expired — click to re-login.', color: '#e74c3c' },
          4002: { title: '👤 Character Not Found', action: 'This character may have been deleted. Return to character select.', color: '#e67e22' },
          4003: { title: '⏳ Server Initializing', action: 'The server is still loading. Please wait and try again.', color: '#f39c12' },
          4004: { title: '🗄️ Database Error', action: 'Could not load character data. The database may be offline.', color: '#e74c3c' },
          4005: { title: '🚪 Room Full', action: 'This room has reached max capacity. Try again shortly.', color: '#e67e22' },
          4006: { title: '⚠️ Invalid Request', action: 'The join request was malformed. Please refresh and try again.', color: '#e74c3c' },
          1006: { title: '📡 Connection Lost', action: 'The server may have crashed or restarted. Attempting to reconnect...', color: '#3498db' },
        };

        const info = closeCode ? errorInfo[closeCode] : null;
        const title = info?.title || 'DISCONNECTED';
        const action = info?.action || 'Attempting to reconnect to server...';
        const borderColor = info?.color || '#e74c3c';

        return (
          <div style={{ ...connectionStatusStyle, borderColor }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={{...connectionStatusTitleStyle, margin: 0, color: borderColor }}>{title}</h2>
              <button
                onClick={() => setConnectionBannerVisible(false)}
                style={{ background: 'transparent', border: 'none', color: '#ffaaaa', cursor: 'pointer', fontSize: '16px', padding: '0 5px' }}
                title="Hide Diagnostics"
              >
                ✕
              </button>
            </div>
            <div style={{ ...connectionStatusMessageStyle, marginTop: '8px' }}>{action}</div>
            {closeCode && (
              <div style={{ color: '#888', marginTop: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                Error Code: {closeCode}
              </div>
            )}
          </div>
        );
      })()}

      {/* Rendering/Physics/MP Diagnostics moved to the 🛠 kit launcher
          (renderingDiag / physicsDiag / mpDiagnostics tools) — 2026-05-30. */}

      {isExpanded ? (
        <>
          {/* Player Info — docked top-left unit frame (not a draggable window). */}
          {DevConfig.systems.playerInfoPanel && (
            <div
              id="player-info"
              style={{
                position: 'absolute',
                left: 16,
                top: 16,
                width: HUD_PANEL_DEFAULTS.playerInfo.width,
                pointerEvents: 'auto',
                zIndex: 100,
              }}
            >
              <PlayerInfoPanel player={player} hudStyle={hudTome.frameStyle} />
            </div>
          )}

          {/* Action Bar — replaced by touch controls when active */}
          {DevConfig.systems.actionBar && isHotbarHydrated && (
            <>
              {player.class === 'rogue' && <StarCounter player={player} />}
              {isTouchControlsEnabled() ? (
                /* In touch controls mode, the mobile radial cluster replaces the hotbar. */
                <div style={{ position: 'fixed', bottom: 120, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 90 }}>
                  <BuffBar playerId={player.id} />
                </div>
              ) : (
                <ActionBarHudCluster
                  player={player}
                  compact={compact}
                  hudStyle={hudTome.hotbarStyle}
                  onAction={onAction}
                  buildActionBarUpdate={buildActionBarUpdate}
                />
              )}
            </>
          )}

          {/* Quest Tracker — always-visible objective list docked top-right.
              Self-hides when there are no active quests (the full journal is
              the Q window). */}
          <QuestTracker player={player} hudStyle={hudTome.trackerStyle} />

          {/* Chat — docked bottom-left (WoW-style), not a draggable window.
              Compact landscape (W2a): right-column peek above the scaled
              action band, width clamped right of the player/target column. */}
          {DevConfig.systems.chat && (
            <div
              id="chat-system"
              data-testid="hud-chat-dock"
              style={chatDockStyle(compactChatInset)}
            >
              <ChatPanel compactWidth={compact.isCompact ? compact.chatWidth : undefined} hudStyle={hudTome.chatStyle} />
            </div>
          )}

          {/* System Panels */}
          {(
            <HUDPanel
              id="system-panels"
              initialX={calculateX(HUD_PANEL_DEFAULTS.systemPanels.position)}
              initialY={calculateY(HUD_PANEL_DEFAULTS.systemPanels.position)}
              initialWidth={HUD_PANEL_DEFAULTS.systemPanels.width}
              style={{ pointerEvents: 'auto' }}>
              <div className="system-panels" style={{ position: 'relative', top: 'auto', right: 'auto' }}>
                {onOpenJobAdvancement && (
                  <MiniJobDisplay
                    currentJob={player.job}
                    level={player.level}
                    onOpenJobAdvancement={onOpenJobAdvancement}
                    style={{ marginBottom: '10px' }}
                  />
                )}
                {DevConfig.systems.miniStatsDisplay && onOpenStats && (
                  <MiniStatsDisplay
                    stats={player.stats || {}}
                    level={player.level}
                    onOpenStats={onOpenStats}
                    statPoints={player.statPoints || 0}
                  />
                )}
              </div>
            </HUDPanel>
          )}
        </>
      ) : (
        <div style={collapsedHUDStyle}>HUD Collapsed</div>
      )}

      {/* Quick Menu */}
      {DevConfig.systems.quickMenu && (
        <div
          data-testid="hud-quick-menu-dock"
          style={quickMenuContainerStyle(quickMenuBottomInset, quickMenuRightInset)}
        >
          <QuickMenu
            currentMapId={levelId}
            maxHeight={compact.isCompact ? compact.quickMenuMaxHeight : undefined}
            width={compact.isCompact ? compact.quickMenuWidth : undefined}
            statPoints={player.statPoints || 0}
            skillPoints={player.skillPoints || 0}
          />
        </div>
      )}

      {/* The DEV-BAR windows (Camera / Simulation & Tests / Map Info / Map
          Picker) are engine windows now — WindowHost mounts them; DebugToolbox
          toggles them via windowBridge (HUD fold 2026-06-12, ratchet 6→0).
          EditorToolsWindow was deleted: ✏️ is a pure editor-mode toggle. */}

      <style>{hudGlobalStyles}</style>
    </div>
  );
};
